import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { MeResponse, StartAssessmentResponse } from 'api';
import type { CountryCode } from 'types/countries';
import {
    normalizeEnabledCountryCodes,
    payloadHasEnabledCountryCodes,
    pickEnabledCountryCodes,
} from 'utils/countries';
import {
    normalizeEnabledFeatures,
    payloadHasEnabledFeatures,
    pickEnabledFeatures,
} from 'utils/features';
import { getTokens, logout } from '../auth/auth-middleware';
import { setTokens } from '../auth/auth-slice';
import { loadMe } from './user-middleware';

interface UserData extends MeResponse {
    /** @deprecated Prefer useProfilePicture() from hooks/useMyAccount */
    url: string;
    enabled_features: string[];
    enabled_country_codes: CountryCode[];
}

type UserDataType = {
    entity: UserData;
    error: string;
    status: 'idle' | 'loading';
};

const emptyUser = (): UserData => ({
    email: '',
    file_number: '',
    firstname: '',
    id: 0,
    lastname: '',
    middlename: '',
    role: '',
    status: '',
    url: '',
    language_setting: 'EN',
    completed_assessment: '',
    timestamp: '',
    enabled_features: [],
    enabled_country_codes: [],
});

const initialState: UserDataType = {
    entity: emptyUser(),
    error: '',
    status: 'idle',
};

const userSlice = createSlice({
    name: 'root/user',
    initialState,
    reducers: {
        setEnabledFeatures: (state, action: PayloadAction<unknown>) => {
            state.entity.enabled_features = normalizeEnabledFeatures(action.payload);
        },
        setEnabledCountryCodes: (state, action: PayloadAction<unknown>) => {
            state.entity.enabled_country_codes = normalizeEnabledCountryCodes(action.payload);
        },
        setAssessmentStartTimestamps: (state, action: PayloadAction<StartAssessmentResponse>) => {
            state.entity.assessment_started_at = action.payload.assessment_started_at;
            if ('assessment_first_saved_at' in action.payload) {
                state.entity.assessment_first_saved_at = action.payload.assessment_first_saved_at;
            }
        },
        disableEnabledFeature: (state, action: PayloadAction<string>) => {
            const name = action.payload;
            if (!name) {
                return;
            }
            state.entity.enabled_features = (state.entity.enabled_features || []).filter(
                (feature) => feature !== name
            );
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadMe.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadMe.fulfilled, (state, action) => {
                const data = action.payload.data;
                state.entity = {
                    ...state.entity,
                    ...data,
                    enabled_features: normalizeEnabledFeatures(data.enabled_features),
                    enabled_country_codes: normalizeEnabledCountryCodes(data.enabled_country_codes),
                };
                state.status = 'idle';
            })
            .addCase(loadMe.rejected, (state) => {
                state.status = 'idle';
            })
            .addCase(getTokens.fulfilled, (state, action) => {
                state.entity.enabled_features = pickEnabledFeatures(action.payload.data);
                state.entity.enabled_country_codes = pickEnabledCountryCodes(action.payload.data);
            })
            .addCase(setTokens, (state, action) => {
                if (payloadHasEnabledFeatures(action.payload)) {
                    state.entity.enabled_features = pickEnabledFeatures(action.payload);
                }
                if (payloadHasEnabledCountryCodes(action.payload)) {
                    state.entity.enabled_country_codes = pickEnabledCountryCodes(action.payload);
                }
            })
            .addCase(logout.fulfilled, () => {
                return {
                    entity: emptyUser(),
                    status: 'idle',
                    error: '',
                };
            });
    },
});

export const {
    setEnabledFeatures,
    setEnabledCountryCodes,
    setAssessmentStartTimestamps,
    disableEnabledFeature,
} = userSlice.actions;
export default userSlice.reducer;
